/*
 * Completes the last grid row before the "Load more" button by borrowing the first products
 * of the next page, so a promo banner (or any product count) never leaves a widow row.
 * When "Load more" appends that next page, the borrowed products are not shown twice.
 * Rows are measured from the real layout, so it works for any banner size/position and for
 * the different desktop/mobile column counts and ordering.
 */
if (!customElements.get('collection-row-fill')) {
  customElements.define(
    'collection-row-fill',
    class CollectionRowFill extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.ajaxinateId;
        this.grid = document.querySelector(`#AjaxinateLoop[data-ajaxinate-id="${id}"]`);
        this.pagination = document.querySelector(`#AjaxinatePagination[data-ajaxinate-id="${id}"]`);

        if (!this.grid) return;

        this.pages = new Map();
        this.requestUpdate = this.requestUpdate.bind(this);
        this.update = this.update.bind(this);

        // New pages are appended to the grid, the button is replaced/removed in the pagination
        this.observer = new MutationObserver(this.requestUpdate);
        this.observer.observe(this.grid, {childList: true});
        if (this.pagination) this.observer.observe(this.pagination, {childList: true, subtree: true});

        window.addEventListener('resize', this.requestUpdate);

        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('resize', this.requestUpdate);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      requestUpdate() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(this.update);
      }

      async update() {
        this.rafId = null;
        if (this.busy) return;

        this.removeDuplicates();

        const nextUrl = this.pagination?.querySelector('[data-load-more]')?.href;
        if (!nextUrl) return;

        const missing = this.missingCells();
        if (missing < 1) return;

        this.busy = true;
        try {
          const nextItems = await this.getPageItems(nextUrl);
          const shownIds = new Set(this.productItems().map((item) => item.id));
          const borrowed = nextItems.filter((item) => !shownIds.has(item.id)).slice(0, missing);

          borrowed.forEach((item) => this.grid.insertAdjacentHTML('beforeend', item.outerHTML));
        } catch (error) {
          console.warn('collection-row-fill:', error);
        } finally {
          this.busy = false;
        }
      }

      productItems() {
        return Array.from(this.grid.children).filter((item) => item.id.startsWith('product-item--'));
      }

      // The borrowed products come again with the next page: keep the first (borrowed) copy
      removeDuplicates() {
        const seen = new Set();
        this.productItems().forEach((item) => {
          if (seen.has(item.id)) {
            item.remove();
          } else {
            seen.add(item.id);
          }
        });
      }

      missingCells() {
        const columns = getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length;
        if (columns < 2) return 0;

        // Only items that take part in the layout (skips desktop-only/mobile-only banners, style tags, etc.)
        const visible = Array.from(this.grid.children).filter((item) => item.offsetParent !== null && item.offsetHeight > 0);
        const products = visible.filter((item) => item.matches('.product-item:not(.product-item--featured-image)'));
        if (!products.length) return 0;

        // A regular product card is exactly one column wide
        const columnWidth = Math.min(...products.map((item) => item.offsetWidth));
        const lastRowTop = Math.max(...visible.map((item) => item.offsetTop));

        // Cells taken in the last row, including banners spanning down from the rows above
        let usedCells = 0;
        visible.forEach((item) => {
          const coversLastRow = item.offsetTop <= lastRowTop && item.offsetTop + item.offsetHeight > lastRowTop + 1;
          if (coversLastRow) usedCells += Math.max(1, Math.round(item.offsetWidth / columnWidth));
        });

        return Math.max(0, columns - usedCells);
      }

      async getPageItems(url) {
        if (!this.pages.has(url)) {
          const request = fetch(url)
            .then((response) => response.text())
            .then((html) => {
              const page = new DOMParser().parseFromString(html, 'text/html');
              const grid = page.querySelector(`#AjaxinateLoop[data-ajaxinate-id="${this.dataset.ajaxinateId}"]`);
              return grid ? Array.from(grid.children).filter((item) => item.id.startsWith('product-item--')) : [];
            });
          this.pages.set(url, request);
        }

        return this.pages.get(url);
      }
    }
  );
}
